import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import DashboardLayout from "./DashboardLayout";
import { Users, Gamepad2, Activity, Trophy } from "lucide-react";
import { GAME_BY_ID } from "@/lib/games";

export default function DashboardOverview() {
  const [children, setChildren] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const [c, s] = await Promise.all([api.get("/children"), api.get("/sessions")]);
      setChildren(c.data);
      setSessions(s.data);
      setLoading(false);
    })();
  }, []);

  const totalPlays = sessions.length;
  const avgScore =
    sessions.length === 0
      ? 0
      : Math.round(sessions.reduce((a, b) => a + b.score, 0) / sessions.length);

  return (
    <DashboardLayout>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl">Visão geral</h1>
          <p className="text-slate-500 mt-1">Resumo da atividade no Mundo NeuroKids.</p>
        </div>
        <Link
          to="/dashboard/children"
          className="bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition"
          data-testid="overview-manage-children"
        >
          Gerenciar crianças
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <StatCard label="Crianças cadastradas" value={children.length} icon={<Users size={20} />} testId="stat-children" />
        <StatCard label="Sessões registradas" value={totalPlays} icon={<Gamepad2 size={20} />} testId="stat-sessions" />
        <StatCard label="Pontuação média" value={avgScore} icon={<Trophy size={20} />} testId="stat-avg-score" />
      </div>

      <h2 className="text-xl font-semibold mb-4">Atividade recente</h2>
      <div className="dash-card">
        {loading ? (
          <div className="text-slate-500 text-sm">Carregando…</div>
        ) : sessions.length === 0 ? (
          <div className="text-slate-500 text-sm flex items-center gap-2">
            <Activity size={16} /> Nenhuma sessão registrada ainda. Use o modo criança para começar.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="recent-sessions-table">
            <thead className="text-slate-500 text-left">
              <tr>
                <th className="py-2 font-medium">Data</th>
                <th className="py-2 font-medium">Jogo</th>
                <th className="py-2 font-medium">Pontuação</th>
                <th className="py-2 font-medium">Duração</th>
              </tr>
            </thead>
            <tbody>
              {sessions.slice(0, 10).map((s) => {
                const g = GAME_BY_ID[s.game_id];
                const child = children.find((c) => c.id === s.child_id);
                return (
                  <tr key={s.id} className="border-t border-slate-100">
                    <td className="py-2">{new Date(s.created_at).toLocaleString("pt-BR")}</td>
                    <td className="py-2">
                      <span className="mr-2">{g?.emoji}</span>
                      {g?.name || s.game_id}
                      {child && <span className="text-slate-400"> · {child.name}</span>}
                    </td>
                    <td className="py-2 font-semibold">{s.score}</td>
                    <td className="py-2">{(s.duration_ms / 1000).toFixed(1)}s</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </DashboardLayout>
  );
}

function StatCard({ label, value, icon, testId }) {
  return (
    <div className="dash-card flex items-center gap-4" data-testid={testId}>
      <div className="w-10 h-10 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center">
        {icon}
      </div>
      <div>
        <div className="text-2xl font-semibold text-slate-900">{value}</div>
        <div className="text-sm text-slate-500">{label}</div>
      </div>
    </div>
  );
}
